import React from 'react';
import { useTranslation } from 'react-i18next';
import { buildTiktokOpsPagerItems } from '@/tiktok-platform/utils/tiktokPageSelection';
import '@/styles/tiktok-platform/tiktok-ops-pager.css';

/** TikTok 运营台列表分页：左「共 N 条」，右「上一页 / 页码 / 下一页 + 每页条数」 */
export const TIKTOK_OPS_PAGE_SIZE_OPTIONS = [30, 100, 200, 500];

export const TIKTOK_OPS_DEFAULT_PAGE_SIZE = 30;

/** 本地 MySQL 列表 pageSize 上限（与分页器选项对齐；平台 OpenAPI 另有自身上限） */
export const TIKTOK_OPS_MAX_PAGE_SIZE = 500;

function resolveTotalPages(page, pageSize, total, hasNext) {
  const size = Math.max(1, Number(pageSize) || TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  if (typeof total === 'number' && total >= 0) {
    return Math.max(1, Math.ceil(total / size) || 1);
  }
  const current = Math.max(1, Number(page) || 1);
  return hasNext ? current + 1 : current;
}

export default function TiktokOpsPager({
  page = 1,
  pageSize = TIKTOK_OPS_DEFAULT_PAGE_SIZE,
  total = 0,
  loading = false,
  disabled = false,
  /** 未知 total 时，用本页行数判断是否还有下一页 */
  pageItemCount,
  hasNext,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = TIKTOK_OPS_PAGE_SIZE_OPTIONS,
  ariaLabel = '',
  className = '',
}) {
  const { t } = useTranslation('workbench');
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const safeTotal = typeof total === 'number' && total >= 0 ? total : 0;
  const knownTotal = typeof total === 'number' && total >= 0;

  const inferredHasNext = typeof hasNext === 'boolean'
    ? hasNext
    : (typeof pageItemCount === 'number'
      ? pageItemCount >= safeSize
      : (knownTotal ? safePage < Math.ceil(safeTotal / safeSize) : false));

  const totalPages = resolveTotalPages(safePage, safeSize, knownTotal ? safeTotal : undefined, inferredHasNext);
  const pageItems = buildTiktokOpsPagerItems(safePage, totalPages);
  const busy = Boolean(loading || disabled);
  const canPrev = !busy && safePage > 1;
  const canNext = !busy && (knownTotal ? safePage < totalPages : inferredHasNext);

  const options = (Array.isArray(pageSizeOptions) && pageSizeOptions.length
    ? pageSizeOptions
    : TIKTOK_OPS_PAGE_SIZE_OPTIONS
  ).map((item) => (typeof item === 'number' ? { value: item, label: t('listPager.pageSize', { count: item }) } : item));

  const handlePageSizeChange = (event) => {
    const next = Number(event.target.value);
    if (!Number.isFinite(next) || next <= 0) return;
    onPageSizeChange?.(next);
  };

  return (
    <nav
      className={`tko-ops-pager${className ? ` ${className}` : ''}`}
      role="navigation"
      aria-label={ariaLabel || t('listPager.nav')}
    >
      <span className="tko-ops-pager__total">
        {t('listPager.total', { count: knownTotal ? safeTotal : (typeof pageItemCount === 'number' ? pageItemCount : 0) })}
      </span>

      <div className="tko-ops-pager__controls">
        <button
          type="button"
          className="tko-btn tko-btn--secondary tko-btn--sm"
          disabled={!canPrev}
          onClick={() => onPageChange?.(safePage - 1)}
        >
          {t('listPager.prev')}
        </button>

        <div className="tko-ops-pager__pages" role="group" aria-label={t('listPager.pages')}>
          {pageItems.map((item) => {
            if (typeof item === 'string') {
              return (
                <span key={item} className="tko-ops-pager__ellipsis" aria-hidden="true">
                  …
                </span>
              );
            }
            const active = item === safePage;
            return (
              <button
                key={item}
                type="button"
                className={`tko-ops-pager__page${active ? ' is-active' : ''}`}
                disabled={busy || active}
                aria-label={t('listPager.pageN', { page: item })}
                aria-current={active ? 'page' : undefined}
                onClick={() => onPageChange?.(item)}
              >
                {item}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="tko-btn tko-btn--secondary tko-btn--sm"
          disabled={!canNext}
          onClick={() => onPageChange?.(safePage + 1)}
        >
          {t('listPager.next')}
        </button>

        {onPageSizeChange ? (
          <label className="tko-ops-pager__size">
            <span className="tko-ops-pager__size-label">{t('listPager.perPage')}</span>
            <select
              className="tko-ops-pager__size-select"
              value={safeSize}
              disabled={busy}
              aria-label={t('listPager.perPageCount')}
              onChange={handlePageSizeChange}
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || t('listPager.pageSize', { count: opt.value })}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    </nav>
  );
}
